// 위험성평가 OS API 클라이언트
// 백엔드: app/site_records/router.py (검증 완료)
//  - 270만건 사고DB 기반 위험요인·5단계 대책 초안(suggest)
//  - 라이프사이클: 생성 → 행저장 → 완료(보존·서명·next_due) → 재평가(동일작업 복제) → due 알림
import { api } from './api'
import type { MatrixSize } from './riskMatrix'

export type RAAssessmentType = 'initial' | 'periodic' | 'occasional' | 'continuous'
export type RAMethod = 'freq_sev_3' | 'freq_sev_5' | 'three_level' | 'checklist' | 'kras'
export type RAStatus = 'draft' | 'in_progress' | 'completed' | 'revised'
export type MeasureTier = 'elimination' | 'substitution' | 'engineering' | 'administrative' | 'ppe'
export type ItemStatus = 'planned' | 'in_progress' | 'done'

export const TIER_LABEL: Record<MeasureTier, string> = {
  elimination: '제거', substitution: '대체', engineering: '공학적', administrative: '관리적', ppe: '보호구',
}

export interface ReductionMeasure {
  tier?: MeasureTier; measure?: string; owner?: string; due?: string; done_at?: string
  /** 기한 산정 근거 — AI가 위험도에서 도출해 미리 채운 값(P3) */
  due_basis?: string
}
export interface LegalBasis { article_no?: string; article_title?: string }

/** §36① '이행' 증빙 사진 (P3) — 경로는 서버가 감추고 idx 로만 접근 */
export interface MeasureEvidence {
  idx: number
  note?: string | null
  mime?: string | null
  uploaded_at?: string | null
  measure_index?: number | null
}

/** 전자서명 (P4) — 이름 텍스트는 서명이 아니다 */
export interface RASignature {
  name?: string
  role?: string
  signature?: string   // dataURL png
  signed_at?: string
}

export interface RAItem {
  id?: string
  seq: number
  unit_task?: string | null
  hazard_category?: string | null
  hazard?: string | null
  cause?: string | null
  current_controls?: string | null
  frequency?: number | null
  severity?: number | null
  risk_score?: number | null
  risk_level?: string | null
  acceptable?: boolean | null
  reduction_measures?: ReductionMeasure[]
  residual_frequency?: number | null
  residual_severity?: number | null
  residual_score?: number | null
  residual_level?: string | null
  legal_basis?: LegalBasis | null
  linked_accident_case_id?: string | null
  status?: ItemStatus
  evidence?: MeasureEvidence[]
}

export interface RiskAssessment {
  id: string
  site_id: string
  /** 작업 단위 관리 — 어느 작업의 평가표인지. null=사업장 단위(구문서) */
  task_id?: string | null
  title: string
  assessment_type: RAAssessmentType
  method: RAMethod
  target_scope?: string | null
  assessed_at?: string | null
  period_start?: string | null
  period_end?: string | null
  next_due_at?: string | null
  participants?: Array<Record<string, unknown>>
  status: RAStatus
  overall_risk_level?: string | null
  summary?: string | null
  approved_by?: string | null
  approved_at?: string | null
  revision_of?: string | null
  source_meta?: { matrix_size?: MatrixSize } & Record<string, unknown>
  items?: RAItem[]
  created_at?: string
  /** P4 — 전자서명(승인자·근로자대표) */
  signatures?: RASignature[]
  /** 고시 §14 — 사전조사 안전보건정보(기계·MSDS·작업표준·과거재해 …). 평가 '전에' 모으는 자료 */
  pre_survey?: Record<string, string | undefined> | null
  /** P4 — 법정 보존기한(완료일 + 3년, 산안법 §36⑤). 보존 중이면 삭제 불가 */
  retention_until?: string | null
  retained?: boolean
}

/** 홈 작업 보드 → 위험성평가 작성 화면 프리필.
 *  작업에 저장된 정보로 입력을 채워 곧바로 AI 도출까지 돌리고,
 *  **항목 선택은 사용자가 한다**(자동 저장 금지 — 일반 위험성평가와 동일한 절차). */
export interface RaPrefill {
  /** 이 평가표·항목이 붙을 작업 */
  task_id?: string
  /** 작업 설명(작업명 + 작업종류 + 위험 힌트) */
  work_description: string
  title?: string
  work_type?: string
  /** 화면 진입 즉시 AI 위험요인 도출까지 실행 */
  auto?: boolean
}

/** P3 — 기한 도래·경과 개선대책 (홈 체크리스트) */
export interface MeasureDueRow {
  assessment_id: string
  assessment_title?: string | null
  item_id: string
  measure_index: number
  unit_task?: string | null
  hazard?: string | null
  measure?: string | null
  owner?: string | null
  due: string
  /** 남은 일수 — 음수면 기한 경과 */
  days: number
  risk_level?: string | null
  has_evidence: boolean
}
export interface MeasuresDue {
  overdue: MeasureDueRow[]
  upcoming: MeasureDueRow[]
  /** 기한이 안 잡힌 대책 수(P3 선주입 이전 데이터) */
  no_due: number
  total_open: number
}

// 270만건 사고DB 기반 초안
export interface SuggestCase {
  case_id: string
  source: string
  similarity?: number
  summary: string
  hazard: string
  prevention: string
  labels?: Record<string, string>
}
export interface SuggestHazard {
  unit_task?: string
  hazard_category?: string
  hazard?: string
  cause?: string
  current_controls?: string
  frequency?: number
  severity?: number
  residual_frequency?: number
  residual_severity?: number
  measures?: ReductionMeasure[]
  case_ref?: string
  legal_basis?: LegalBasis
}
export type PenaltyLevel = 'criminal' | 'fine' | 'general'
export interface ComplianceArticle { article_no: string; article_title: string; penalty_level?: PenaltyLevel; must_check?: boolean }
export interface ComplianceSummary {
  session_id?: string; criminal_count?: number; matched_count?: number; must_check_count?: number; grade?: string | null
  articles?: ComplianceArticle[]
}
export interface SuggestResult {
  work_desc: string
  cases: SuggestCase[]
  hazards: SuggestHazard[]
  measures: Array<{ tier: MeasureTier; measure: string }>
  compliance?: ComplianceSummary | null
}

// 현장 사진 분석(로컬 Gemma) 결과 — 사용자 확인용
export interface PhotoHazard {
  agent: string
  category: string
  missing_control: string
  expected_accident: string
  observation: string
  seed_line: string
  /** 이 위험요인이 나온 사진의 순번(0-base). 확인 팝업에서 해당 사진을 함께 표시. */
  photo_index?: number | null
}
export interface PhotoAnalysis {
  photo_count: number
  scenes: string[]
  hazards: PhotoHazard[]
}

// 근로자 참여(안전소통 제보)
export interface ParticipationCandidate {
  report_id: string; report_type: 'hazard' | 'near_miss'; title: string; content: string
  location_text?: string | null; severity?: string | null; reporter_name?: string | null; created_at?: string | null
}
export interface ParticipationData {
  candidates: ParticipationCandidate[]
  participants: Array<{ name?: string | null; worker_id?: string | null; via: string; at?: string | null }>
}

export interface RADueItem {
  id: string; title: string; next_due_at: string; overdue: boolean; d_day: number; overall_risk_level?: string | null
}

const base = (siteId: string) => `/sites/${siteId}/risk-assessments`

export const riskAssessmentApi = {
  list: (siteId: string) => api.get<{ items: RiskAssessment[] }>(base(siteId)).then(r => r.data),
  get: (siteId: string, id: string) => api.get<RiskAssessment>(`${base(siteId)}/${id}`).then(r => r.data),
  create: (siteId: string, body: Partial<RiskAssessment> & { items: RAItem[] }) =>
    api.post<RiskAssessment>(base(siteId), body).then(r => r.data),
  update: (siteId: string, id: string, body: Partial<RiskAssessment>) =>
    api.patch<RiskAssessment>(`${base(siteId)}/${id}`, body).then(r => r.data),
  // 백엔드는 본문이 RAItemIn 배열 그 자체를 기대(객체 래핑 X)
  saveItems: (siteId: string, id: string, items: RAItem[]) =>
    api.put<RiskAssessment>(`${base(siteId)}/${id}/items`, items).then(r => r.data),
  complete: (siteId: string, id: string, body: {
    assessed_at?: string; period_months?: number; approved_by?: string
    signatures?: RASignature[]
  }) => api.post<RiskAssessment>(`${base(siteId)}/${id}/complete`, body).then(r => r.data),

  // ── P3 개선대책 이행 추적 (§36① '이행' = 1,000만원) ──────────────────────
  /** 기한이 지났거나 임박한 개선대책 — 홈에서 "이번 주 할 일"로 보여준다 */
  measuresDue: (siteId: string, withinDays = 7) =>
    api.get<MeasuresDue>(`/sites/${siteId}/measures-due`, { params: { within_days: withinDays } })
      .then(r => r.data),
  /** 이행 증빙 사진 업로드 = 이행 완료 처리 (차감 없음 — 이행 기록에 비용을 물리면 아무도 안 한다) */
  uploadEvidence: (siteId: string, raId: string, itemId: string, photo: File,
                   opts?: { note?: string; measureIndex?: number }) => {
    const fd = new FormData()
    fd.append('photo', photo)
    if (opts?.note) fd.append('note', opts.note)
    if (opts?.measureIndex != null) fd.append('measure_index', String(opts.measureIndex))
    return api.post<{ ok: boolean; item: { id: string; status: ItemStatus; reduction_measures: ReductionMeasure[]; evidence: MeasureEvidence[] } }>(
      `${base(siteId)}/${raId}/items/${itemId}/evidence`, fd,
      { timeout: 120_000, headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data)
  },
  evidenceUrl: (siteId: string, raId: string, itemId: string, idx: number) =>
    `/api${base(siteId)}/${raId}/items/${itemId}/evidence/${idx}`,
  deleteEvidence: (siteId: string, raId: string, itemId: string, idx: number) =>
    api.delete(`${base(siteId)}/${raId}/items/${itemId}/evidence/${idx}`).then(r => r.data),
  reassess: (siteId: string, id: string, body?: { title?: string; assessment_type?: RAAssessmentType }) =>
    api.post<RiskAssessment>(`${base(siteId)}/${id}/reassess`, body ?? {}).then(r => r.data),
  remove: (siteId: string, id: string) => api.delete(`${base(siteId)}/${id}`).then(r => r.data),
  // 270만건 사고DB 기반 위험요인·5단계 대책 초안 (matrixSize=빈도·강도 범위, existingControls=현재 안전조치)
  suggest: (siteId: string, workDesc: string, matrixSize: MatrixSize = '5x5', existingControls = '', topK = 12, seedHazards?: string[]) =>
    api.post<SuggestResult>(`${base(siteId)}/suggest`,
      { work_desc: workDesc, top_k: topK, matrix_size: matrixSize, existing_controls: existingControls,
        ...(seedHazards && seedHazards.length ? { seed_hazards: seedHazards } : {}) },
      { timeout: 180_000 }).then(r => r.data),
  // 현장 사진 분석(로컬 Gemma) — 사용자 확인용 위험요인만 반환(생성 X)
  analyzePhotos: (siteId: string, photos: File[]) => {
    const fd = new FormData()
    photos.slice(0, 8).forEach(p => fd.append('photos', p))
    return api.post<PhotoAnalysis>(`${base(siteId)}/analyze-photos`, fd,
      { timeout: 240_000, headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data)
  },
  due: (siteId: string, withinDays = 30) =>
    api.get<{ items: RADueItem[]; today: string }>(`${base(siteId)}-due`, { params: { within_days: withinDays } }).then(r => r.data),
  // 근로자 참여 — 안전소통 제보를 위험요인 후보로
  participation: (siteId: string, days = 180) =>
    api.get<ParticipationData>(`${base(siteId)}-participation`, { params: { days } }).then(r => r.data),
  // ⑤ 상시평가 — 신규 동종사고로 재검토 권고(무료는 캡 밖 사고 블러)
  reviewRadar: (siteId: string, days = 30) =>
    api.get<ReviewRadarResult>(`${base(siteId)}-review-radar`, { params: { days } }).then(r => r.data),
  // ⑥ 동종사고 → 우리 위험성평가 전수 영향분석(작업별 대비됨/신규 판정).
  //    이미 분석한 사고면 저장된 결과를 그대로 반환(차감 없음). force=true 만 재분석·1회 차감.
  accidentImpact: (siteId: string, accidentId: string, matrixSize = '5x5', force = false) =>
    api.post<AccidentImpact>(`${base(siteId)}/accident-impact`,
      { accident_id: accidentId, matrix_size: matrixSize, force }, { timeout: 240_000 }).then(r => r.data),
  // ⑦ 선택한 신규 위험요인을 실제 평가표에 반영(완료본은 수시 재평가본 생성). 위험성평가 1회 차감.
  accidentApply: (siteId: string, accidentId: string,
                  applies: Array<{ ra_id: string; items: ImpactNewItem[] }>) =>
    api.post<AccidentApplyResult>(`${base(siteId)}/accident-apply`,
      { accident_id: accidentId, applies }, { timeout: 120_000 }).then(r => r.data),
  // ⑧ 이미 우리 평가표에 반영된 사고 목록 — '반영 완료' 표시용(차감 없음)
  appliedAccidents: (siteId: string) =>
    api.get<AppliedAccidents>(`${base(siteId)}-applied-accidents`).then(r => r.data),
  // 위험성평가표 엑셀(무료=워터마크 PDF / 유료=xlsx) — blob
  exportTable: (siteId: string, id: string) =>
    api.get<Blob>(`${base(siteId)}/${id}/export`, { responseType: 'blob', timeout: 120_000 }),
  // ③ 증빙 PDF(유료) — blob 응답
  evidence: (siteId: string, id: string) =>
    api.get<Blob>(`${base(siteId)}/${id}/evidence`, { responseType: 'blob', timeout: 120_000 }),
  // 법규 준수 현장 체크리스트 — 법규준수점검 기능과 동일 생성(compliance_session 재사용)
  complianceChecklist: (sessionId: string) =>
    api.get<Blob>(`/safety-rule-match/export/${sessionId}`, { responseType: 'blob', timeout: 120_000 }),
}

export interface ReviewRec {
  ra_id: string; ra_title: string; assessed_at?: string | null
  accident: { locked: boolean; id?: string; title?: string; accident_date?: string; accident_type?: string; relevance?: number; match_reasons?: string[] }
}
export interface ReviewRadarResult { recommendations: ReviewRec[]; locked: number; view_limit: number | null }

// ── 동종사고 → 우리 위험성평가 반영 ────────────────────────────────────────
/** 우리 평가에 없어 새로 추가할 위험요인(그대로 accident-apply 로 되돌려 보낸다) */
export interface ImpactNewItem {
  hazard: string
  hazard_category?: string | null
  cause?: string | null
  current_controls?: string | null
  unit_task?: string | null
  frequency?: number | null
  severity?: number | null
  risk_score?: number | null
  risk_level?: string | null
  residual_frequency?: number | null
  residual_severity?: number | null
  measures?: Array<{ tier: MeasureTier; measure: string }>
}
/** 이미 우리 평가에 반영돼 있는 위험 — 어느 기존 항목이 커버하는지 */
export interface ImpactCovered {
  hazard: string
  covered_seq?: number | null
  /** 기존 대책이 이 사고를 막기에 부족할 때의 보완점(없으면 빈 문자열) */
  gap_note?: string
  existing_hazard?: string | null
  existing_unit_task?: string | null
  existing_controls?: string | null
  existing_risk_level?: string | null
}
/** 평가표(=작업) 1개에 대한 분석 결과 */
export interface ImpactAssessment {
  ra_id: string
  ra_title: string
  target_scope?: string | null
  ra_status: string
  assessed_at?: string | null
  existing_count: number
  /** 이 사고의 위험이 이 작업에 실제로 적용되는가 */
  applicable: boolean
  relevance_reason?: string
  covered: ImpactCovered[]
  new: ImpactNewItem[]
}
/** 이 사고로 실제 평가표에 반영된 내역(반영 완료 시) */
export interface AppliedDetail {
  applied: boolean
  applied_at?: string | null
  total_items: number
  assessments: Array<{
    ra_id: string; ra_title: string; ra_status: string; revision_of?: string | null
    items: Array<{
      seq: number; hazard?: string | null; hazard_category?: string | null
      risk_level?: string | null; risk_score?: number | null; measures: number
    }>
  }>
  /** 이미 반영된 위험요인 텍스트(분석의 '신규' 항목과 대조용) */
  hazards: string[]
}
export interface AccidentImpact {
  accident: {
    id: string; accident_date?: string | null; industry_label?: string | null
    accident_type?: string | null; title?: string | null; body?: string | null
    fatality_count?: number
  }
  assessments: ImpactAssessment[]
  matrix_size?: string
  totals: {
    assessments_analyzed: number; assessments_truncated: number
    related: number; covered: number; new: number
  }
  /** 저장된 이전 분석을 그대로 보여준 것인지(true = 사용량 차감 없음) */
  cached?: boolean
  analyzed_at?: string | null
  /** 실제 반영 내역 — 반영 완료 사고를 다시 열었을 때 표시 */
  applied?: AppliedDetail
}
export interface AccidentApplyResult {
  applied: Array<{
    ra_id: string; ra_title: string; source_ra_id: string
    revision_of?: string | null; added: number; created_revision: boolean
  }>
  total_added: number
  accident_id: string
}
/** 이미 위험성평가에 반영된 사고들(반영 완료 표시용) */
export interface AppliedAccidents {
  items: Array<{
    accident_id: string
    /** 반영된 위험요인 항목 수 / 반영된 평가표 수 */
    item_count: number
    ra_count: number
    applied_at?: string | null
  }>
  accident_ids: string[]
}

// ── 기존 안전문서 이관(Import) — KRAS·자체 양식 → Heinrich RA ────────────────────
export interface ImportItem {
  process?: string
  unit_task?: string
  hazard: string
  cause?: string | null
  current?: string | null
  frequency?: number | null
  severity?: number | null
  risk_text?: string | null
  risk_score?: number | null
  risk_level?: string | null
  acceptable?: boolean | null
  measure?: string | null
  due?: string | null
  owner?: string | null
  done_at?: string | null
  /** KRAS 계열 문서의 부가 컬럼 — 우리 항목 모델에 그대로 저장된다 */
  category?: string | null
  legal_text?: string | null
  residual_text?: string | null
  residual_frequency?: number | null
  residual_severity?: number | null
  residual_score?: number | null
  residual_level?: string | null
}
/** 분석이 판별할 수 있는 문서 종류(저장 여부와 별개 — TBM·점검표는 안내만 한다). */
export type ImportKind = 'risk_assessment' | 'jsa' | 'work_permit' | 'tbm' | 'site_overview'
                       | 'checklist' | 'training'
/** 업로드 **칸**으로 고를 수 있는 종류 = 저장까지 지원하는 3종. */
export type ImportSlotKind = 'risk_assessment' | 'jsa' | 'work_permit' | 'site_overview'

export interface ImportJsaRow {
  step: string; hazard: string; safeguards?: string; measures?: string
  frequency_before?: number | null; severity_before?: number | null
  risk_level_before?: string | null; pic?: string
}
export interface ImportPermit {
  permit_no?: string | null
  permit_type: string
  title: string
  work_description: string
  location?: string | null
  work_start_at?: string | null
  work_end_at?: string | null
  issued_at?: string | null
  approver?: string | null
  /** 기간을 읽어 정한 값. null 이면 사용자가 골라야 한다. */
  status?: string | null
  /** 사용자가 검토 화면에서 고른 상태(백엔드가 status 보다 우선한다) */
  status_override?: string | null
  needs_confirm?: boolean
  safety_items: { name: string; checked: boolean; note?: string }[]
}
export interface ImportTbm {
  tbm_date: string | null
  title: string
  work_description: string
  hazards: string[]
  safety_measures: string[]
  attendees_note?: string
}
export interface ImportDoc {
  filename: string
  rel_path?: string
  doc_type: string
  /** 종류 판별 결과. 위험성평가가 아니면 각자의 페이로드가 채워진다. */
  doc_kind?: ImportKind | null
  method?: RAMethod
  matrix_size?: MatrixSize | null
  assessed_at?: string | null
  task_name?: string
  /** 문서에서 뽑은 작업명 후보와 그 근거(head=머리표 / content=본문 / filename=파일명) */
  task_hint?: string
  task_hint_source?: 'head' | 'content' | 'filename'
  task_group?: number
  task_confidence?: 'high' | 'medium' | 'low'
  meta?: Record<string, unknown>
  columns?: Record<string, string>
  used_llm?: boolean
  needs_confirm?: boolean
  items: ImportItem[]
  /** 문서 머리의 설비·화학물질 목록(KRAS 1페이지 등) — 반영 시 사업장 프로필로 */
  profile?: {
    equipment?: { name: string; quantity?: number | null }[]
    chemicals?: { name: string; monthly_amount?: string | null; usage_time?: string | null }[]
  } | null
  jsa_rows?: ImportJsaRow[]
  permit?: ImportPermit
  tbms?: ImportTbm[]
  warnings?: string[]
  /** 이관 대상이 아니라 건너뛴 문서(TBM·점검표 등) — 사유는 warnings[0]. */
  skipped?: boolean
  /** 사용자가 고른 칸과 자동 판별이 다를 때의 다른 후보(강제로 바꾸지 않는다). */
  suggested_kind?: ImportKind | null
  /** AI 해석 중 일부 조각을 못 읽었다 — 반영하면 원본 행이 누락된다. */
  partial?: boolean
  /** 이미 반영을 마친 것과 동일한 파일(sha256) — 검토 화면 경고 + 기본 해제. */
  duplicate?: boolean
  missing_rows?: number
  /** 이 문서를 읽은 경로: 'llm' 이면 표 형식이 달라 AI가 의미로 옮긴 것. */
  engine?: 'llm' | null
}
export interface ImportGroup {
  index: number
  name: string
  confidence: 'high' | 'medium' | 'low'
  docs: number[]
  kinds: string[]
}
export interface ImportJobStatus {
  job_id: string
  status: 'running' | 'done' | 'error'
  steps: { key: string; status: string; note?: string }[]
  docs: ImportDoc[]
  errors: Record<string, string>
  groups?: ImportGroup[]
  supported_kinds?: string[]
  kind_labels?: Record<string, string>
}

/** 이관 진단 — 2층 법규 대조 / 3층 사고 대조 결과 */
export interface DiagnoseLegal {
  available: boolean
  reason?: string
  session_id?: string
  matched_count?: number
  must_check_count?: number
  criminal_count?: number
  missing_count?: number
  missing?: { article_no: string; article_title: string
              penalty_level?: string; must_check?: boolean }[]
}
export interface DiagnoseAccidents {
  available: boolean
  reason?: string
  /** 무료 플랜에서 3층이 잠긴 작업 */
  locked?: boolean
  cases?: number
  gap_count?: number
  gaps?: { hazard: string; cause?: string; measures?: string[]
           risk_level?: string | null; case_summary?: string }[]
}
export interface DiagnoseResult {
  task_id: string
  task_name: string
  ra_items: number
  jsa_rows: number
  legal: DiagnoseLegal
  accidents: DiagnoseAccidents
}
export interface DiagnoseStatus {
  job_id: string
  status: 'running' | 'done' | 'error'
  steps: { key: string; status: string; note?: string; deep?: boolean }[]
  results: DiagnoseResult[]
  errors: Record<string, string>
  summary: { tasks: number; legal_missing: number
             accident_gaps: number; locked_tasks: number }
}

export const docImportApi = {
  /**
   * kinds = 파일별 업로드 칸(files 와 같은 순서·같은 개수). 빈 문자열이면 그 파일만 자동 판별.
   * 칸을 나눠도 **요청은 한 번**이다 — 슬롯마다 나눠 보내면 동시 실행 가드에 409 가 난다.
   * kind = 전체 일괄 지정(칸이 하나뿐인 화면용).
   */
  analyze: (siteId: string, files: File[],
            opts?: { kind?: ImportSlotKind; kinds?: (ImportSlotKind | '')[] }) => {
    const fd = new FormData()
    files.forEach(f => fd.append('files', f))
    const kind = opts?.kind
    const kinds = opts?.kinds
    if (kinds && kinds.length === files.length) kinds.forEach(k => fd.append('kinds', k))
    else if (kind) fd.append('kind', kind)
    // api 기본 헤더가 application/json 이라 FormData 는 명시 필요(axios 가 boundary 채움)
    return api.post<{ job_id: string; files: number; kind?: string | null
                      kinds?: Record<string, string> | null }>(
      `/sites/${siteId}/doc-import/analyze`, fd,
      { timeout: 60_000, headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data)
  },
  status: (siteId: string, jobId: string) =>
    api.get<ImportJobStatus>(`/sites/${siteId}/doc-import/jobs/${jobId}`).then(r => r.data),
  apply: (siteId: string, jobId: string, docs: ImportDoc[], buildProfile = true) =>
    api.post<{
      created: { kind?: string; id?: string; ra_id?: string; task_id: string
                 task_name: string; items?: number; rows?: number
                 assessed_at?: string; tbm_date?: string; permit_no?: string | null
                 status?: string; superseded?: boolean; filename: string }[]
      tasks_created: number
      /** 종류별 저장 건수 — {risk_assessment: 5, jsa: 2, work_permit: 3, tbm: 30} */
      counts?: Record<string, number>
      /** 구본으로 밀려난 평가표 수(이력은 보존, 재평가 기한만 해제) */
      superseded?: number
      /** 이관은 '기준 문서 완비' 도장을 찍지 않는다 — 항상 false */
      baseline_marked?: boolean
      /** 저장에 실패한 문서. 비어 있지 않으면 '일부만 반영'이다. */
      failed?: { filename: string; kind?: string; error?: string }[]
      profile: { equipment: string[]; ppe: string[]; processes: string[]
                 chemicals?: string[] } | null
    }>(`/sites/${siteId}/doc-import/apply`,
       { job_id: jobId, docs, build_profile: buildProfile },
       { timeout: 120_000 }).then(r => r.data),

  /** 이관 진단 시작 — 2층 전 작업, 3층은 플랜에 따라 상위 N개만 */
  diagnose: (siteId: string, taskIds: string[]) =>
    api.post<{ job_id: string; reused: boolean; tasks?: number; deep_tasks?: number
               plan_code: string; deep_limit: number; preview: boolean }>(
      `/sites/${siteId}/doc-import/diagnose`, { task_ids: taskIds },
      { timeout: 60_000 }).then(r => r.data),
  diagnoseStatus: (siteId: string, jobId: string) =>
    api.get<DiagnoseStatus>(
      `/sites/${siteId}/doc-import/diagnose/${jobId}`).then(r => r.data),
}
